/* eslint-disable no-console */
/* eslint-disable max-len */
import { Project, Agent, Home, EntityClass, OrganizationPasswordPolicy } from '@moderepo/mode-internal-apis';
import { capitalizeAll, ChartSettings, CountryData, getLanguageByCode, Language, SelectInputOption, TIME_ZONES }
    from '@moderepo/mode-app-components';
import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { TFunction } from 'i18next';
import { RESTRICTED_PASSWORDS, RESTRICTED_PASSWORD_CHARACTERS, SUPPORTED_LANGUAGES } from './constants';
import { ProjectTypeHandlersManager } from './ritzExtensionUtils';
import { ROUTE_WITH_ORG_ID_PREFIX } from '../containers/app/App';
import { DeviceRegistrationWizardSettings, EntitiesFeatureSettings } from './ritzUIExtensionUtils';



// Define a NonEmptyArray type to enforce an array to have at least 1 element. This way we don't need to check for empty all the time
// and this can also have the compiler make sure we don't accidentally initialize the array without any element
export type NonEmptyArray<T> = [T, ...T[]];
export type ReadonlyNonEmptyArray<T> = readonly [T, ...T[]];

/**
 * A utility for remove the "readonly" for all the attributes in the interface T and make them mutable.
 * Usage:
 *      interface MyInterface {
 *          readonly x: number;
 *          readonly y: string;
 *      }
 *
 *      const obj: MyInterface = {
 *          x: 1, y: "test"
 *      };
 *      obj.x = 5;          // This is not allowed because "x" is readonly
 *
 *      const mutableObj: Mutable<MyInterface> = {
 *          x: 1, y: "test"
 *      };
 *      mutableObj.x = 5;   // It is now ok to change "x"
 */
export type Mutable<T> = {
    -readonly[P in keyof T]: T[P];
};



// When an agent's password expired, we will take the user to the My Account page to show the password expired message. If the password
// expiration policy is BLOCK, we will keep taking the agent here whenever they try to go anywhere else in the app. However, if the password
// expiration policy is WARN, we will NOT keep taking the user to the My Account page. We will only do it once a session. Therefore, we need
// to keep track of whether the agent has already seen the alert so that we don't show it again. We will store this info in uiState under
// this cache key.
export const PASSWORD_EXPIRED_ALERT_SEEN_CACHE_KEY = 'passwordExpiredAlertSeen';



/**
 * The user is logged into DEFAULT org but trying to access a project in x_company org
 *     Current page URL: http://ops.tinkermode.com/orgs/x_company/projects/1234
 *     We will redirect the user to http://ops.tinkermode.com/orgs/default/projects/1234
 *
 * The user is logged into x_company org but trying to access a project in default org
 *     Current page org: http://ops.tinkermode.com/projects/1234
 *     Or current page org: http://ops.tinkermode.com/orgs/default/projects/1234
 *     We will redirect the user to http://ops.tinkermode.com/orgs/x_company/projects/1234
 *
 * @param fromOrgId e.g. "default", "x_company", or "" (empty strings means default company)
 * @param toOrgId e.g. "default", "x_company", (no empty string)
 */
export const redirectUserToAnotherOrganization = (fromOrgId: string, toOrgId: string) => {
    const searchParams = new URLSearchParams(window.location.search);

    // pathname will be something like this "/projects/1234" or "/orgs/default/projects/1234" or "/orgs/x_company/projects/1234"
    const pathname = window.location.pathname;
    const fromOrgIdWithPrefix = `${ROUTE_WITH_ORG_ID_PREFIX}${fromOrgId}`;
    const toOrgIdWithPrefix = `${ROUTE_WITH_ORG_ID_PREFIX}${toOrgId}`;

    // If pathname starts with "/orgs/xxx" then we will replace that "/orgs/xxx" with "/orgs/[TO_ORG_ID]"
    // Else we will just need to add "/orgs/[TO_ORG_ID]" to the pathname
    const newPathName = pathname.startsWith(fromOrgIdWithPrefix)
        ? window.location.pathname.replace(fromOrgIdWithPrefix, toOrgIdWithPrefix)
        : `${toOrgIdWithPrefix}${window.location.pathname}`;

    // Build a new URL including the domain, pathname, searchParams, and hash and then take the user to that URL.
    const newURL = `${window.location.origin}${newPathName}${searchParams}${window.location.hash}`;
    window.location.href = newURL;
};



/**
  * Check that the agent preferred language is based on the current project and agent. We will use the language settings
  * base on this order
  * - Use agent profile's preferences (if Agent is not null)
  * - Use Project's settings if agent is not logged in or does not have a preference
  */
export const getPreferredLanguage = (project: Project | undefined, agent: Agent | undefined, defaultLanguage: Language): Language => {
    let result: Language = defaultLanguage;

    // First check if there is an agent and he has preferred language set
    if (agent && agent.preferences && agent.preferences.locale) {
        result = getLanguageByCode(SUPPORTED_LANGUAGES, agent.preferences.locale) || result;
    } else if (project && project.supportToolSettings && project.supportToolSettings.locale) {
        // Else check if there is a project and it has a language setting
        result = getLanguageByCode(SUPPORTED_LANGUAGES, project.supportToolSettings.locale) || result;
    }

    return result;
};



/**
 * Find the CountryData object for the given local
 */
export const getCountryByLocale = (countryListJson: any, locale: string, defaultCountry?: CountryData): CountryData | undefined => {
    const localeSeparator = locale.indexOf('-') >= 0 ? '-' : '_';
    const parts = locale.toUpperCase().split(localeSeparator);
    const countryCode = parts.length > 1 ? parts[1] : parts[0];
    if (countryListJson[countryCode]) {
        return countryListJson[countryCode];
    }

    // Return US if country not found
    return defaultCountry;
};

export const asNumber = (v: unknown): number => {
    if (typeof v === 'number') {
        return v;
    }
    return 0;
};

export const asString = (v: unknown): string => {
    if (typeof v === 'string') {
        return v;
    }
    return '';
};



/**
 * We use this in a few places therefore, we will create a type of this so that it would be more type strict. So instead of harding the string
 * 'none' everywhere, we would use these constant and type like this
 */
export const NoneValue = 'none';
export type TypeNone = typeof NoneValue;



/**
 * Helper function to the an entity ID from the URL and cast the value to a number. The entityId can be from route params or URL query params
 * From route params: /projects/1234/users/5678
 * From URL query params: /?projectId=1234&userId=6789
 * @param entityName
 * @returns
 */
export const useGetURLParamEntityIdAsNumber = (entityName: string): number | undefined => {
    const params = useParams();
    const [queryParams] = useSearchParams();

    const entityId = useMemo(() => {
        if (params[entityName] !== undefined) {
            return Number(params[entityName]);
        }

        if (queryParams.get(entityName) !== null) {
            return Number(queryParams.get(entityName));
        }

        return undefined;
    }, [entityName, params, queryParams]);

    return entityId;
};



/**
 * Helper function to the an entity ID from the URL and cast the value to a string. The entityId can be from route params or URL query params
 * From route params: /projects/1234/users/5678
 * From URL query params: /?projectId=1234&userId=6789
 * @param entityName
 * @returns
 */
export const useGetURLParamEntityIdAsString = (entityName: string): string | undefined => {
    const params = useParams();
    const [queryParams] = useSearchParams();

    const entityId = useMemo(() => {
        if (params[entityName] !== undefined) {
            return String(params[entityName]);
        }

        if (queryParams.get(entityName) !== null) {
            return String(queryParams.get(entityName));
        }

        return undefined;
    }, [entityName, params, queryParams]);

    return entityId;
};



/**
 * Helper function to the an entity ID from the URL and cast the value to a boolean. The entityId can be from route params or URL query params
 * From route params: /projects/1234/users/true
 * From URL query params: /?projectId=1234&showUsers=true
 * @param entityName
 * @returns
 */
export const useGetURLParamEntityIdAsBoolean = (entityName: string): boolean | undefined => {
    const params = useParams();
    const [queryParams] = useSearchParams();

    const entityId = useMemo(() => {
        // NOTE: boolean values in the URL are STRING, not boolean even if the values are 'true', 'false'. We can't
        // simply cast them to Boolean
        if (params[entityName] === 'true') {
            return true;
        }

        if (params[entityName] === 'false') {
            return false;
        }

        if (queryParams.get(entityName) === 'true') {
            return true;
        }

        if (queryParams.get(entityName) === 'false') {
            return false;
        }

        return undefined;
    }, [entityName, params, queryParams]);

    return entityId;
};



/**
 * Given a timeZone value e.g. 'UTC', 'America/Los_Angeles', etc... and a locale, return the localized user friendly name of the timeZone.
 * This function will return undefined if the timeZone is invalid, it is not one of the known timeZone
 *
 * @param locale - e.g. 'en-US', 'ja-JP'
 * @param timeZone - 'UTL', 'America/Los_Angeles', etc...
 * @returns
 */
export const getTimeZoneDisplayName = (locale: string, timeZone: string | undefined): string | undefined => {
    if (timeZone) {
        try {
            // Create Intl object with the specified options
            const intl = new Intl.DateTimeFormat(
                locale,             // The 'locale' is used for translation. If 'locale === 'ja-JP' then Intl will return timeZoneName in Japanese
                {
                    timeZone,               // The timeZone we want to use
                    timeZoneName: 'long',   // Return the long version of the time zone name
                },
            );

            /**
             * Format the current date based on the date/time options we set and return the values in multiple parts.
             * This will return an array of date/time parts like this
             *      [{
             *          type: 'year', value: 2021
             *      }, {
             *          type: 'month', value: 9
             *      }, {
             *          type: 'timeZoneName', value: "Coordinated Universal Time"
             *      }]
             * We will find the part that has type === 'timeZoneName' and return it's value
             */
            const parts = intl.formatToParts(new Date());
            const timeZoneNamePart = parts.find((part) => {
                return part.type === 'timeZoneName';
            });

            if (timeZoneNamePart) {
                return timeZoneNamePart.value;
            }
        } catch (error) {
        // timeZone is probably invalid
        }
    }

    return undefined;
};



/**
 * Create an Array of Home options to be used for the homeId SELECT input fields.
 */
export const createHomeOptions = (homes: readonly Home[] | undefined): readonly SelectInputOption<number>[] => {
    if (homes) {
        return homes.map((home): SelectInputOption<number> => {
            return {
                value: home.id,
                label: `${home.id}${home.name ? ` - ${home.name}` : ''}`,
            };
        });
    }
    return [];
};



/**
 * Create an Array of timeZone options to be used for the timeZone SELECT input fields.
 * if isOptional is true, we will add a 'None' option to the beginning of the list
 */
export const createTimeZoneOptions = (isOptional: boolean = false, noneLabel: string = 'None'): readonly SelectInputOption<string>[] => {
    return [
        ...(isOptional
            ? [{
                value: '',
                label: noneLabel,
                icon : ' ',
            }]
            : []
        ),
        ...TIME_ZONES.map((timeZone) => {
            return {
                label: timeZone.timeZone,
                value: timeZone.timeZone,
            };
        }),
    ];
};


/**
 * Create an Array of Entity Class options to be used for the entityClass SELECT input fields.
 */
export const createEntityClassOptions = (entityClasses: readonly EntityClass[] | undefined): readonly SelectInputOption<string>[] => {
    if (entityClasses) {
        return entityClasses.map((entityClass): SelectInputOption<string> => {
            return {
                value: entityClass.entityClass,
                label: entityClass.displaySettings.name,
            };
        });
    }
    return [];
};



/**
 * A helper function to check if Entities feature is supported
 */
export const isEntitiesFeatureEnabled = (project: Project | undefined, loggedInAgent: Agent | undefined): boolean => {
    const projectUIConfig = ProjectTypeHandlersManager.getInstance().getProjectCustomUIHandler(
        project?.supportToolSettings.projectType,
    )?.getCustomUIConfig(loggedInAgent, project);

    return projectUIConfig?.enabledFeatures?.entities === true || typeof projectUIConfig?.enabledFeatures?.entities === 'object';
};


/**
 * Get the project's entities feature settings if it is configured. The projectUIConfig.enabledFeatures.entities can be set to true/false
 * or an Object. If it is only set to "true" then return undefined.
 */
export const getEntitiesFeatureSettings = (project: Project | undefined, loggedInAgent: Agent | undefined): EntitiesFeatureSettings | undefined => {
    const projectUIConfig = ProjectTypeHandlersManager.getInstance().getProjectCustomUIHandler(
        project?.supportToolSettings.projectType,
    )?.getCustomUIConfig(loggedInAgent, project);

    return typeof projectUIConfig?.enabledFeatures?.entities === 'object' ? projectUIConfig.enabledFeatures.entities : undefined;
};


/**
 * A helper function to check if Alerting feature is supported
 */
export const isAlertingFeatureEnabled = (project: Project | undefined, loggedInAgent: Agent | undefined): boolean => {
    const projectUIConfig = ProjectTypeHandlersManager.getInstance().getProjectCustomUIHandler(
        project?.supportToolSettings.projectType,
    )?.getCustomUIConfig(loggedInAgent, project);

    return projectUIConfig?.enabledFeatures?.alerting === true;
};


/**
 * A helper function to check if the device registration wizard feature is supported
 */
export const isDeviceRegistrationWizardEnabled = (project: Project | undefined, loggedInAgent: Agent | undefined): boolean => {
    const projectUIConfig = ProjectTypeHandlersManager.getInstance().getProjectCustomUIHandler(
        project?.supportToolSettings.projectType,
    )?.getCustomUIConfig(loggedInAgent, project);

    return projectUIConfig?.enabledFeatures?.deviceRegistrationWizard !== undefined
        && projectUIConfig?.enabledFeatures?.deviceRegistrationWizard.deviceClassesSettings.length > 0
        && isEntitiesFeatureEnabled(project, loggedInAgent);
};


/**
 * Given a "DeviceRegistrationWizardSettings" and a deviceClass, return whether the provided deviceClass can be configured with the
 * device registration wizard. A project might have 5 device classes for example but maybe only 3 device classes are configured in the
 * deviceRegistrationWizardSettings so we need to make sure the provided deviceClass is one of them
 */
export const isDeviceRegistrationWizardEnabledForDeviceClass = (
    deviceRegistrationWizardSettings: DeviceRegistrationWizardSettings | undefined,
    deviceClass: string | undefined,
): boolean => {
    return deviceRegistrationWizardSettings?.deviceClassesSettings.some((settings) => {
        return settings.deviceClass === deviceClass;
    }) ?? false;
};



/**
 * Create the export options for the Chart and cache it. We will pass this to the Chart settings later
 */
export const createChartExportSettings = (trans: TFunction): ChartSettings['exportSettings'] => {
    return {
        items: [{
            label: '...',
            menu : [{
                label: capitalizeAll(trans('charts.export_menu.image.label')),
                menu : [
                    { type: 'png', label: 'PNG' },
                    { type: 'jpg', label: 'JPG' },
                    { type: 'svg', label: 'SVG' },
                    { type: 'pdf', label: 'PDF' },
                ],
            }, {
                label: capitalizeAll(trans('charts.export_menu.data.label')),
                menu : [
                    { type: 'json', label: 'JSON' },
                    { type: 'csv', label: 'CSV' },
                ],
            }],
        }],
    };
};



/**
 * Validate the NUMBER inputValue against the inputRules and return an Error string if the value is invalid
 */
export const genericValidateNumberInput = (
    inputValue: unknown,
    inputRules: {
        readonly required: boolean;
        readonly min?: number | undefined;
        readonly max?: number | undefined;
        readonly options?: readonly {
            readonly label: string;
            readonly value: unknown;
        }[] | undefined;
    },
    isNewInput: boolean,
    trans: TFunction,
): string | undefined => {

    // undefined, and null inputValue are considered no value. Empty string are also considered as no value but only if `isNewInput`.
    // If not `isNewInput` then empty string will be validated because Number("") is 0 and we need to make sure 0 allowed based on the rules.
    const hasValue = inputValue !== undefined && inputValue !== null
        && (!isNewInput || (typeof inputValue !== 'string' || inputValue.trim().length > 0));

    if (inputRules.required && !hasValue) {
        // If input is required but inputValue is not defined then return error
        return trans('error.input.generic.empty');
    }

    if (hasValue) {
        const actualValue = Number(inputValue);
        if (Number.isNaN(actualValue)) {
            return trans('error.input.generic.not_a_number');
        }
        if (inputRules.min !== undefined && inputRules.max !== undefined && (actualValue < inputRules.min || actualValue > inputRules.max)) {
            // If the inputRules contain both min/max settings and the value is out of range then use this error message
            return trans('error.input.generic.range', { min: inputRules.min, max: inputRules.max });
        }
        if (inputRules.min && actualValue < inputRules.min) {
            // If only the min is set then we only need to check if the value is less than min
            return trans('error.input.generic.too_low', { min: inputRules.min });
        }
        if (inputRules.max && actualValue > inputRules.max) {
            // If only the max is set then we only need to check if the value is greater than max
            return trans('error.input.generic.too_high', { max: inputRules.max });
        }
        if (inputRules.options) {
            // If options is provided, make sure the actualValue is one of the option values provided
            const match = inputRules.options.find((option) => {
                return option.value === actualValue;
            });
            if (match === undefined) {
                return trans('error.input.generic.invalid');
            }
        }
    }

    return undefined;
};



/**
 * Validate the STRING inputValue against the inputRules and return an Error string if the value is invalid
 */
export const genericValidateStringInput = (
    inputValue: unknown,
    inputRules: {
        readonly required: boolean;
        readonly min?: number | undefined;
        readonly max?: number | undefined;
        readonly regex?: string | undefined;
        readonly options?: readonly {
            readonly label: string;
            readonly value: unknown;
        }[] | undefined;
    },
    isNewInput: boolean,
    trans: TFunction,
): string | undefined => {

    // undefined, and null inputValue are considered no value. Empty string are also considered as no value but only if `isNewInput`.
    // If not `isNewInput` then empty string will be validated.
    const hasValue = inputValue !== undefined && inputValue !== null
        && (!isNewInput || (typeof inputValue !== 'string' || inputValue.trim().length > 0));

    if (inputRules.required && !hasValue) {
        // If input is required but inputValue is not defined or is empty then return error
        return trans('error.input.generic.empty');
    }

    if (hasValue) {

        if (typeof inputValue !== 'string') {
            return trans('error.input.generic.invalid');
        }

        // trim the input before checking other rules
        const actualValue = inputValue.trim();

        if (
            (inputRules.min !== undefined && inputRules.max !== undefined)
            && (actualValue.length < inputRules.min || actualValue.length > inputRules.max)
        ) {
            // If the inputRules contain both min/max settings and the value's length is out of range then use this error message
            return trans('error.input.generic.length', { min: inputRules.min, max: inputRules.max });
        }
        if (inputRules.min && actualValue.length < inputRules.min) {
            // If only the min is set then we only need to check if the value's length is less than min
            return trans('error.input.generic.too_short', { min: inputRules.min });
        }
        if (inputRules.max && actualValue.length > inputRules.max) {
            // If only the max is set then we only need to check if the value's length is greater than max
            return trans('error.input.generic.too_long', { max: inputRules.max });
        }
        if (inputRules.regex !== undefined && !(new RegExp(inputRules.regex)).test(actualValue)) {
            // Validate the input against the regex
            return trans('error.input.generic.invalid');
        }
        if (inputRules.options && actualValue !== undefined) {
            // If options is provided, make sure the actualValue is one of the option values provided
            const match = inputRules.options.find((option) => {
                return option.value === actualValue;
            });
            if (match === undefined) {
                return trans('error.input.generic.invalid');
            }
        }
    }

    return undefined;
};



/**
 * Validate the BOOLEAN inputValue against the inputRules and return an Error string if the value is invalid
 */
export const genericValidateBooleanInput = (
    inputValue: unknown,
    inputRules: {
        readonly required: boolean;
    },
    trans: TFunction,
): string | undefined => {

    // undefined, null are considered no value
    const hasValue = inputValue !== undefined && inputValue !== null;

    if (inputRules.required && !hasValue) {
        // If input is required but inputValue is not defined then return error
        return trans('error.input.generic.empty');
    }

    // If inputValue is provided but it is not a type of boolean (true/false) then return error
    if (hasValue && typeof inputValue !== 'boolean') {
        return trans('error.input.generic.invalid');
    }

    return undefined;
};



/**
 * Validate the ARRAY inputValue against the inputRules and return an Error string if the value is invalid
 */
export const genericValidateArrayInput = (
    inputValue: unknown,
    inputRules: {
        readonly required: boolean;
    },
    trans: TFunction,
): string | undefined => {

    // undefined, null, empty strings inputValue are considered no value
    const hasValue = inputValue !== undefined && inputValue !== null && (typeof inputValue !== 'string' || inputValue.trim().length > 0);

    if (inputRules.required && !hasValue) {
        // If input is required but inputValue is not defined or is empty then return error
        return trans('error.input.generic.empty');
    }

    if (hasValue) {
        // first, the value must be a string
        if (typeof inputValue !== 'string') {
            return trans('error.input.generic.invalid');
        }

        try {
            const value = JSON.parse(inputValue);
            if (!(value instanceof Array)) {
                // If the inputValue is successfully parsed but the result is NOT an array then show invalid input error
                return trans('error.input.generic.invalid');
            }
        } catch (error) {
            return (error as Error).message;
        }
    }

    return undefined;
};



/**
 * Validate the OBJECT inputValue against the inputRules and return an Error string if the value is invalid
 */
export const genericValidateObjectInput = (
    inputValue: unknown,
    inputRules: {
        readonly required: boolean;
    },
    trans: TFunction,
): string | undefined => {

    // undefined, null, empty strings inputValue are considered no value
    const hasValue = inputValue !== undefined && inputValue !== null && (typeof inputValue !== 'string' || inputValue.trim().length > 0);

    if (inputRules.required && !hasValue) {
        // If input is required but inputValue is not defined or is empty then return error
        return trans('error.input.generic.empty');
    }

    if (hasValue) {
        // first, the value must be a string
        if (typeof inputValue !== 'string') {
            return trans('error.input.generic.invalid');
        }

        try {
            const value = JSON.parse(inputValue);
            if (typeof value !== 'object' || value instanceof Array) {
                // If the inputValue is successfully parsed but the result is NOT an Object or it is an Object but it is also an array then
                // show invalid input error. Arrays are OBJECTS also considered Object so we can't just check if typeof ==='object'
                return trans('error.input.generic.invalid');
            }
        } catch (error) {
            return (error as Error).message;
        }
    }

    return undefined;
};



/**
 * Validate the password against the passwordPolicy and return an Error string if the value is invalid
 */
export const genericValidatePassword = (
    password: string | undefined,
    passwordPolicy: OrganizationPasswordPolicy,
): string | undefined => {

    if (!password || password.trim().length <= 0) {
        return 'error.input.password.empty';
    }
    const trimmedPassword = password.trim();
    if (trimmedPassword.length < passwordPolicy.minLength) {
        return 'error.input.password.too_short';
    }
    if (Array.from(trimmedPassword.toLowerCase()).find((c) => {
        return RESTRICTED_PASSWORD_CHARACTERS.includes(c);
    }) !== undefined) {
        return 'error.input.password.contain_restricted_characters';
    }
    if (RESTRICTED_PASSWORDS.includes(trimmedPassword.toLowerCase())) {
        return 'error.input.password.restricted';
    }
    if (passwordPolicy.mustHaveLowercase && !(/[a-z]/.test(trimmedPassword))) {
        return 'error.input.password.missing_lowercase_letter';
    }
    if (passwordPolicy.mustHaveUppercase && !(/[A-Z]/.test(trimmedPassword))) {
        return 'error.input.password.missing_uppercase_letter';
    }
    if (passwordPolicy.mustHaveNumber && !(/[0-9]/.test(trimmedPassword))) {
        return 'error.input.password.missing_number';
    }
    if (passwordPolicy.mustHaveNonAlphanum && !(/[^a-zA-Z0-9:]/.test(trimmedPassword))) {
        return 'error.input.password.missing_non_alphanum';
    }

    return undefined;
};


/**
 * Type guard function to check an "obj" is actually an object BUT not an Array. Using Javascript "typeof obj === 'object'"" will return true
 * if obj is an object or an array. Sometimes we want to specifically check if it is an object but not array therefore we always have to do multiple
 * checks. This is just a helper function to do all those checks in 1 call.
 */
export const isObjectLiteral = (obj: unknown): obj is object => {
    return obj !== undefined && obj !== null && typeof obj === 'object' && !(obj instanceof Array);
};


/**
 * Helper function to check if an object is empty. We use this in a few places so will make it a function so that it can be reused
 */
export const isEmptyObject = (obj) => {
    return Object.keys(obj).length === 0;
};



/**
 * Given an object, recursively sort the object's attributes by its keys and return the sorted object. For example:
 *
 * From:
 * sourceObj = {
 *      name: "robot",
 *      description: "my robot"
 * }
 *
 * To:
 * result = {
 *      description: "my robot"
 *      name: "robot",
 * }
 */
export const sortObjectAttributes = (sourceObj: object): object => {
    if (sourceObj) {
        if (sourceObj instanceof Array) {
            const result: object = sourceObj.map((value) => {
                if (value !== null && typeof value === 'object') {
                    return sortObjectAttributes(value);
                }
                return value;
            });
            return result;
        }
        if (typeof sourceObj === 'object') {
            const result: object = {};
            Object.keys(sourceObj).sort().forEach((key) => {
                const value = sourceObj[key];
                if (value !== null && typeof value === 'object') {
                    result[key] = sortObjectAttributes(value);
                } else {
                    result[key] = value;
                }
            });
            return result;
        }
    }
    return sourceObj;
};



/**
 * We use settings for customization in many places and before we use these settings, we always have to validate their values to make sure
 * they are valid since they are configured by the admin.
 * This is a reusable helper function to check if a setting value for is a valid string.
 */
export const isValidSettingStringValue = (value: unknown, required?: boolean, allowEmpty?: boolean, enumValues?: readonly string[]): boolean => {
    if (value === undefined || value === null) {
        // If value is not provided, we can return true/false right away depending on whether or not the value is required
        if (required) {
            return false;
        }
        return true;
    }

    if (typeof value !== 'string') {
        return false;
    }

    if (value.trim().length === 0 && !allowEmpty) {
        return false;
    }

    // If enumValues is provided then make sure the value is in one of the enumValues
    if (enumValues && !enumValues.includes(value)) {
        return false;
    }

    return true;
};


/**
 * We use settings for customization in many places and before we use these settings, we always have to validate their values to make sure
 * they are valid since they are configured by the admin.
 * This is a reusable helper function to check if a setting value for is a valid number.
 */
export const isValidSettingNumberValue = (value: unknown, required?: boolean, allowDecimal?: boolean, min?: number, max?: number): boolean => {
    if (value === undefined || value === null) {
        // If value is not provided, we can return true/false right away depending on whether or not the value is optional
        if (required) {
            return false;
        }
        return true;
    }

    if (typeof value !== 'number') {
        return false;
    }

    if (!allowDecimal && !Number.isInteger(value)) {
        return false;
    }

    if (min !== undefined && value < min) {
        return false;
    }

    if (max !== undefined && value > max) {
        return false;
    }

    return true;
};


/**
 * We use settings for customization in many places and before we use these settings, we always have to validate their values to make sure
 * they are valid since they are configured by the admin.
 * This is a reusable helper function to check if a setting value for is a valid boolean.
 */
export const isValidSettingBooleanValue = (value: unknown, required?: boolean): boolean => {
    if (value === undefined || value === null) {
        // If value is not provided, we can return true/false right away depending on whether or not the value is optional
        if (required) {
            return false;
        }
        return true;
    }

    if (typeof value !== 'boolean') {
        return false;
    }

    return true;
};



/**
 * Helper function that takes a value and unit and combine them as 1 string, it unit is defined. If unit is not defined then return the
 * value as is.
 */
export const getDisplayValueAndUnit = (value: string | number, unit: string | undefined, trans: TFunction): string => {
    if (unit) {
        return trans('label.value_unit_display', { value, unit });
    }
    return value.toString();
};
